import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from '@/components/common/toast';
import { TextField } from '@/components/forms/TextField';
import { ApiError } from '@/api/client/abpError';
import { ContactDetailFor } from '@/api/types/tenants';
import type { TenantContactDetailCreateDto } from '@/api/types/tenants';
import { TabActions, TabShell } from './TabShell';
import { useTenantDetails, useUpdateContactDetails } from '../api';

interface PersonForm {
  fullName: string;
  email: string;
  phone: string;
  jobDescription: string;
}
interface ContactsForm {
  person1: PersonForm;
  person2: PersonForm;
  billing: PersonForm;
}

const EMPTY: PersonForm = { fullName: '', email: '', phone: '', jobDescription: '' };

function toPerson(dto: TenantContactDetailCreateDto | undefined): PersonForm {
  if (!dto) return { ...EMPTY };
  return {
    fullName: dto.fullName ?? '',
    email: dto.email ?? '',
    phone: dto.phone ?? '',
    jobDescription: dto.jobDescription ?? '',
  };
}

function isEmpty(p: PersonForm): boolean {
  return !p.fullName.trim() && !p.email.trim() && !p.phone.trim() && !p.jobDescription.trim();
}

export function ContactDetailsTab({ tenantId, onCancel }: { tenantId: string; onCancel: () => void }) {
  const toast = useToast();
  const detailsQuery = useTenantDetails(tenantId);
  const updateMut = useUpdateContactDetails(tenantId);
  const { register, handleSubmit, reset } = useForm<ContactsForm>({
    defaultValues: { person1: { ...EMPTY }, person2: { ...EMPTY }, billing: { ...EMPTY } },
  });

  useEffect(() => {
    const list = detailsQuery.data;
    if (!list) return;
    reset({
      person1: toPerson(list.find((x) => x.contactFor === ContactDetailFor.PERSON1)),
      person2: toPerson(list.find((x) => x.contactFor === ContactDetailFor.PERSON2)),
      billing: toPerson(list.find((x) => x.contactFor === ContactDetailFor.BILLING)),
    });
  }, [detailsQuery.data, reset]);

  function build(p: PersonForm, contactFor: ContactDetailFor): TenantContactDetailCreateDto {
    return {
      tenantId,
      contactFor,
      fullName: p.fullName.trim(),
      email: p.email.trim(),
      phone: p.phone.trim(),
      jobDescription: p.jobDescription.trim(),
    };
  }

  function onSubmit(values: ContactsForm) {
    updateMut.mutate(
      {
        tenantContactDetailPerson1CreateDto: build(values.person1, ContactDetailFor.PERSON1),
        // Legacy drops Person2/Billing entirely when every field is blank.
        tenantContactDetailPerson2CreateDto: isEmpty(values.person2)
          ? null
          : build(values.person2, ContactDetailFor.PERSON2),
        tenantContactDetailBillingCreateDto: isEmpty(values.billing)
          ? null
          : build(values.billing, ContactDetailFor.BILLING),
      },
      {
        onSuccess: () => toast.success('Contact details saved.'),
        onError: (err) =>
          toast.error(err instanceof ApiError ? err.message : 'Failed to save contact details.'),
      },
    );
  }

  return (
    <TabShell
      isLoading={detailsQuery.isLoading}
      isError={detailsQuery.isError}
      onRetry={() => detailsQuery.refetch()}
    >
      <TabActions busy={updateMut.isPending} onCancel={onCancel} formId="contactDetailsForm" />
      <form id="contactDetailsForm" onSubmit={handleSubmit(onSubmit)} noValidate>
        <PersonCard title="Contact Details - Person 1" prefix="person1" register={register} />
        <PersonCard title="Contact Details - Person 2" prefix="person2" register={register} />
        <PersonCard title="Billing Details" prefix="billing" register={register} />
      </form>
    </TabShell>
  );
}

function PersonCard({
  title,
  prefix,
  register,
}: {
  title: string;
  prefix: 'person1' | 'person2' | 'billing';
  register: ReturnType<typeof useForm<ContactsForm>>['register'];
}) {
  return (
    <div className="card mb-3">
      <div className="card-header">
        <strong>{title}</strong>
      </div>
      <div className="card-body">
        <div className="row g-3">
          <div className="col-md-6 col-lg-4">
            <TextField label="Full Name" {...register(`${prefix}.fullName`)} />
          </div>
          <div className="col-md-6 col-lg-4">
            <TextField label="Email" type="email" {...register(`${prefix}.email`)} />
          </div>
          <div className="col-md-6 col-lg-4">
            <TextField label="Phone" placeholder="(+000) 000 000 000" {...register(`${prefix}.phone`)} />
          </div>
          <div className="col-md-6 col-lg-4">
            <TextField label="Position" {...register(`${prefix}.jobDescription`)} />
          </div>
        </div>
      </div>
    </div>
  );
}
